import {useState, type JSX} from 'react'
import {FullModal} from '../dialog-modal/full-modal'
import {ActionModal} from '../dialog-modal/action-modal'
import {useUserPreferencesManagement} from '../../hooks/use-user-preferences-management/use-user-preferences-management'
import {Button} from '../base/button/button'
import {ButtonVariant, ButtonSize} from '../base/button/button.types'
import * as Styled from './dev-settings-modal.styled'

interface DevSettingsModalProps {
    isOpen: boolean
    onClose: () => void
}

export function DevSettingsModal({isOpen, onClose}: DevSettingsModalProps): JSX.Element {
    const {allPreferences, deletePreference, isDeleting, currentUser} =
        useUserPreferencesManagement(undefined, {
            fetchAll: isOpen,
            fetchCurrentUser: isOpen,
        })
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [preferenceToDelete, setPreferenceToDelete] = useState<string | null>(null)

    const handleDeleteAllPreferences = () => {
        if (!allPreferences || allPreferences.length === 0) {
            setShowConfirmModal(false)
            return
        }

        allPreferences.forEach((pref) => {
            deletePreference(pref.key)
        })

        setShowConfirmModal(false)
    }

    const handleDeleteSinglePreference = () => {
        if (preferenceToDelete) {
            deletePreference(preferenceToDelete)
            setPreferenceToDelete(null)
        }
    }

    const formatPreferenceValue = (value: unknown): string => {
        if (value === null || value === undefined) {
            return 'null'
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2)
            } catch {
                return '[Unable to stringify object]'
            }
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value)
        }
        return '[Unknown type]'
    }

    return (
        <>
            <FullModal
                label="Dev Settings"
                isOpen={isOpen}
                onClose={onClose}
                title="Dev Settings"
                size="md"
            >
                <Styled.Content>
                    <Styled.Section>
                        <Styled.SectionTitle>User Information</Styled.SectionTitle>
                        <Styled.InfoRow>
                            <Styled.Label>Name</Styled.Label>
                            <Styled.Value>{currentUser?.username || 'N/A'}</Styled.Value>
                        </Styled.InfoRow>
                        <Styled.InfoRow>
                            <Styled.Label>Email</Styled.Label>
                            <Styled.Value>{currentUser?.email || 'N/A'}</Styled.Value>
                        </Styled.InfoRow>
                        <Styled.InfoRow>
                            <Styled.Label>User ID</Styled.Label>
                            <Styled.Value>{currentUser?.user || 'N/A'}</Styled.Value>
                        </Styled.InfoRow>
                        <Styled.InfoRow>
                            <Styled.Label>Roles</Styled.Label>
                            <Styled.Value>
                                {(currentUser?.roles && currentUser.roles.join(', ')) || 'N/A'}
                            </Styled.Value>
                        </Styled.InfoRow>
                    </Styled.Section>

                    {allPreferences && allPreferences.length > 0 && (
                        <Styled.Section>
                            <Styled.SectionTitle>Saved Preferences</Styled.SectionTitle>
                            <Styled.PreferencesList>
                                {allPreferences
                                    .filter(
                                        (pref) =>
                                            pref.value.value !== null &&
                                            pref.value.value !== undefined,
                                    )
                                    .map((pref) => (
                                        <Styled.PreferenceItem key={pref.identity}>
                                            <Styled.PreferenceInfo>
                                                <Styled.PreferenceKey>
                                                    {pref.key}
                                                </Styled.PreferenceKey>
                                                <Styled.PreferenceValue>
                                                    {formatPreferenceValue(pref.value.value)}
                                                </Styled.PreferenceValue>
                                            </Styled.PreferenceInfo>
                                            <Button
                                                variant={ButtonVariant.Icon}
                                                size={ButtonSize.SM}
                                                onPress={() => {
                                                    setPreferenceToDelete(pref.key)
                                                }}
                                            >
                                                ×
                                            </Button>
                                        </Styled.PreferenceItem>
                                    ))}
                            </Styled.PreferencesList>
                        </Styled.Section>
                    )}

                    <Styled.Section>
                        <Styled.SectionTitle>Actions</Styled.SectionTitle>
                        <Styled.ActionsContainer>
                            <Button
                                variant={ButtonVariant.Outlined}
                                onPress={() => {
                                    setShowConfirmModal(true)
                                }}
                                isDisabled={!allPreferences || allPreferences.length === 0}
                            >
                                Delete All User Preferences
                            </Button>
                        </Styled.ActionsContainer>
                    </Styled.Section>
                </Styled.Content>
            </FullModal>

            <ActionModal
                isOpen={showConfirmModal}
                onClose={() => {
                    setShowConfirmModal(false)
                }}
                onConfirm={handleDeleteAllPreferences}
                title="Confirm Deletion"
                message="Do you wish to delete all user preferences?"
                confirmBtnText="OK"
                cancelBtnText="Cancel"
                isLoading={isDeleting}
                closeOnConfirm={false}
            />

            <ActionModal
                isOpen={preferenceToDelete !== null}
                onClose={() => {
                    setPreferenceToDelete(null)
                }}
                onConfirm={handleDeleteSinglePreference}
                title="Confirm Deletion"
                message={`Do you wish to delete the preference "${preferenceToDelete ?? ''}"?`}
                confirmBtnText="OK"
                cancelBtnText="Cancel"
                isLoading={isDeleting}
                closeOnConfirm={false}
            />
        </>
    )
}
